import {
  Component,
  EventEmitter,
  Input,
  Output,
} from "@angular/core";
import { MessageAttachment } from "../../modals/qna/qna.interface";

/**
 * Renders a rich AI bubble: a text message followed by an inline interactive widget chosen
 * by {@code attachment.type}.
 *
 * Adding a new component type means: register a new case in the template's {@code [ngSwitch]}
 * and drop in the per-type Angular component. The wire envelope, the dispatcher contract,
 * and the submission plumbing stay untouched.
 */
@Component({
  selector: "app-rich-message",
  templateUrl: "./rich-message.component.html",
  styleUrl: "./rich-message.component.scss",
  standalone: false,
})
export class RichMessageComponent {
  /** AI-authored text shown in the bubble above the widget. May be empty. */
  @Input() message: string = "";
  /** Attachment descriptor: type + payload + state. */
  @Input() attachment: MessageAttachment | null = null;
  /** Created timestamp for the message header (matches the styling of other AI bubbles). */
  @Input() createdTime: Date | string = new Date();

  /**
   * HTML shown above the widget. For FEEDBACK_RATING, strips lines the backend appends after
   * submit (canonical: "Rated this conversation 4 out of 5.") so they are not duplicated in UI.
   */
  aboveWidgetHtml(): string {
    if (!this.message?.trim()) return "";
    if (this.attachment?.type !== "FEEDBACK_RATING") return this.message;
    return this.stripEmbeddedFeedbackRatingSummary(this.message).trim();
  }

  private stripEmbeddedFeedbackRatingSummary(html: string): string {
    const paragraphOnlyRating = (inner: string): boolean => {
      const plain = inner
        .replace(/<[^>]+>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
      return /^(\s*)(you\s+)?rated\s+this\s+conversation\s+\d+\s+out\s+of\s+\d+\.?\s*$/i.test(
        plain
      );
    };

    let out = html;
    for (const tag of ["p", "div"] as const) {
      const re = new RegExp(`<${tag}\\b[^>]*>([\\s\\S]*?)<\\/${tag}>`, "gi");
      out = out.replace(re, (full, inner: string) =>
        paragraphOnlyRating(inner) ? "" : full
      );
    }
    out = out.replace(
      /(?:^|[\n\r\s])(?:you\s+)?rated\s+this\s+conversation\s+\d+\s+out\s+of\s+\d+\.?/gi,
      ""
    );
    return out.replace(/<p\b[^>]*>\s*<\/p>/gi, "").replace(/<div\b[^>]*>\s*<\/div>/gi, "").trim();
  }

  /** Generic widget submission emitted upward to be sent to the backend. */
  @Output() widgetSubmit = new EventEmitter<{
    type: string;
    response: Record<string, any>;
  }>();

  onWidgetSubmit(response: Record<string, any>): void {
    if (!this.attachment) return;
    this.widgetSubmit.emit({ type: this.attachment.type, response });
  }
}
